import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../api/ApiContext.jsx";
import { useApi } from "../hooks/useApi.js";
import { AsyncBoundary, InlineMessage, errorText } from "../ui/Feedback.jsx";

/**
 * PlanPeriodScreen is the step between choosing a tier and paying for it.
 *
 * A yearly price is a separate object at the payment provider rather than a
 * coupon on the monthly one, so the period is a decision the customer has to make
 * before the checkout exists — this is the only place that knows which price the
 * browser is about to be sent to.
 *
 * The amounts and the saving come from `GET /billing`, where the server read them
 * from the provider. Nothing on this screen computes money: a discount derived
 * from a locally assumed price would advertise a number nobody is charged.
 */
export default function PlanPeriodScreen() {
  const api = useApiClient();
  const navigate = useNavigate();
  const { planId } = useParams();
  const query = useApi(() => api.billing.get(), [api]);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(null);

  async function handleChoose(interval) {
    if (busy) return;
    setBusy(interval);
    setMessage(null);
    try {
      const result = await api.billing.changePlan(planId, interval);
      if (result?.checkoutUrl) {
        // The plan changes when the provider confirms the payment, not here, so
        // the only thing to say is where the browser is going.
        setMessage({ tone: "success", text: "Redirecting to the payment provider…" });
        window.location.assign(result.checkoutUrl);
        return;
      }
      setMessage({ tone: "success", text: "Plan updated — the new limits apply immediately." });
    } catch (err) {
      if (err?.code === "quota_exceeded" || err?.code === "conflict") {
        setMessage({
          tone: "warning",
          text: `${errorText(err, "That plan can't take your current usage.")} Remove feeds first.`,
        });
      } else if (err?.isNotConfigured) {
        setMessage({ tone: "warning", text: errorText(err, "This period is not available.") });
      } else {
        setMessage({ tone: "danger", text: errorText(err, "Could not start the checkout.") });
      }
    } finally {
      setBusy(null);
    }
  }

  async function handleOpenPortal() {
    setMessage(null);
    try {
      const result = await api.billing.updatePayment();
      if (result?.portalUrl) {
        window.location.assign(result.portalUrl);
        return;
      }
      setMessage({ tone: "success", text: "Payment method updated." });
    } catch (err) {
      setMessage({ tone: "danger", text: errorText(err, "Could not open the billing portal.") });
    }
  }

  return (
    <div>
      {message ? <InlineMessage tone={message.tone}>{message.text}</InlineMessage> : null}

      <AsyncBoundary query={query} onBack={() => navigate("/billing")}>
        {(data) => {
          const plan = data.plans.find((option) => option.id === planId);
          if (!plan) {
            return (
              <InlineMessage tone="danger">
                That plan is not in this server's catalogue.{" "}
                <button
                  type="button"
                  onClick={() => navigate("/billing")}
                  style={{
                    color: "var(--text-accent)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    fontSize: "inherit",
                  }}
                >
                  Back to billing
                </button>
                .
              </InlineMessage>
            );
          }
          return (
            <PeriodChooser
              plan={plan}
              paymentsConfigured={data.paymentsConfigured}
              busy={busy}
              onChoose={handleChoose}
              onOpenPortal={handleOpenPortal}
            />
          );
        }}
      </AsyncBoundary>
    </div>
  );
}

/**
 * PeriodChooser renders the two ways to buy one tier.
 *
 * Three states have to be told apart, because they need different words. A period
 * this deployment does not sell is unavailable and offers no button — the server
 * said so, and a price invented here would be a lie about money. A period that is
 * already the customer's plan is not for sale either: a hosted checkout always
 * creates a *new* subscription, so sending an existing subscriber through one
 * would bill them twice, and the honest destination for that is the provider's
 * own portal.
 */
function PeriodChooser({ plan, paymentsConfigured, busy, onChoose, onOpenPortal }) {
  const monthly = plan.prices?.monthly ?? { available: false };
  const yearly = plan.prices?.yearly ?? { available: false };
  const limit =
    plan.feedLimit == null ? "Unlimited calendars" : `Up to ${plan.feedLimit.toLocaleString()} calendars`;
  const sellable = plan.purchasable && !plan.contactOnly;

  return (
    <div style={{ maxWidth: 720 }}>
      <button type="button" className="ical-btn" onClick={() => window.history.back()} style={{ marginBottom: 16 }}>
        ← Back
      </button>

      <p style={{ margin: 0, fontWeight: 500, fontSize: "var(--text-lg)" }}>
        Choose how to pay for {plan.name}
      </p>
      <p style={{ margin: "2px 0 16px", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
        {limit}
        {plan.rateLabel ? ` · ${plan.rateLabel}` : ""}
      </p>

      {plan.current ? (
        <InlineMessage tone="info">
          {plan.name} is already your plan. Changing between monthly and yearly is a change to the
          running subscription, and a checkout here would start a second one — so do it at the payment
          provider instead.{" "}
          <button
            type="button"
            onClick={onOpenPortal}
            style={{
              color: "var(--text-accent)",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              fontSize: "inherit",
            }}
          >
            Open the billing portal
          </button>
          .
        </InlineMessage>
      ) : !sellable ? (
        <InlineMessage tone="warning">
          {plan.contactOnly
            ? `${plan.name} is arranged individually; contact us and we will set it up.`
            : `This server has no price configured for ${plan.name} yet, so it cannot be bought here.`}
        </InlineMessage>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 12,
          marginTop: plan.current || !sellable ? 0 : 16,
        }}
      >
        <PeriodCard
          interval="monthly"
          title="Monthly"
          price={monthly}
          unit="/ month"
          caption="Billed every month. Cancel whenever you like."
          busy={busy === "monthly"}
          disabled={busy != null || !sellable || plan.current || !monthly.available}
          unavailableText="Not available on this server"
          actionLabel="Choose monthly"
          onChoose={onChoose}
        />
        <PeriodCard
          interval="yearly"
          title="Yearly"
          price={yearly}
          unit="/ year"
          caption={
            yearly.discountPercent > 0
              ? `${yearly.amountLabel} for twelve months instead of ${yearly.fullPriceLabel} — you save ${yearly.savingLabel}.`
              : "Billed once a year."
          }
          badge={yearly.discountPercent > 0 ? `Save ${yearly.discountPercent}%` : null}
          busy={busy === "yearly"}
          disabled={busy != null || !sellable || plan.current || !yearly.available}
          unavailableText="Not available on this server"
          actionLabel="Choose yearly"
          onChoose={onChoose}
          highlight
        />
      </div>

      <p style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", marginTop: 14 }}>
        {paymentsConfigured
          ? "You will be redirected to our payment provider. The plan changes once the payment is confirmed — never before."
          : "No payment provider is configured on this server, so nothing can be charged here yet."}
      </p>
    </div>
  );
}

function PeriodCard({
  interval,
  title,
  price,
  unit,
  caption,
  badge,
  busy,
  disabled,
  unavailableText,
  actionLabel,
  onChoose,
  highlight = false,
}) {
  return (
    <div
      className="ical-card"
      data-testid={`period-${interval}`}
      style={highlight ? { border: "2px solid var(--border-accent)" } : undefined}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <p style={{ margin: 0, fontSize: "var(--text-base)", fontWeight: 500 }}>{title}</p>
        {badge ? (
          <span
            className="ical-badge"
            data-testid="period-yearly-discount"
            style={{ background: "var(--bg-accent)", color: "var(--text-accent)" }}
          >
            {badge}
          </span>
        ) : null}
      </div>

      {price.available ? (
        <>
          <p style={{ margin: "10px 0 0", fontSize: "var(--text-2xl)", fontWeight: 500 }}>
            {price.amountLabel}
            <span style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", fontWeight: 400 }}>
              {" "}
              {unit}
            </span>
          </p>
          <p style={{ margin: "6px 0 14px", fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
            {caption}
          </p>
        </>
      ) : (
        <p style={{ margin: "10px 0 14px", fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
          {unavailableText}
        </p>
      )}

      <button
        type="button"
        className={highlight ? "ical-btn ical-btn-primary" : "ical-btn"}
        style={{ width: "100%" }}
        disabled={disabled || !price.available}
        title={!price.available ? "No price is configured for this period on this server" : undefined}
        onClick={() => onChoose(interval)}
      >
        {busy ? "Redirecting…" : actionLabel}
      </button>
    </div>
  );
}
